import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Chip,
  Alert,
  LinearProgress,
  TextField,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Upload as UploadIcon,
  ThumbUp as ThumbUpIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { ChecklistItem, ContentTask } from '../types/ContentTracking';
import ContentTrackingService from './backendservices/ContentTrackingService';

interface ChecklistDialogProps {
  open: boolean;
  onClose: () => void;
  task: ContentTask | null;
  onTaskUpdate: () => void;
}

// Default checklist items based on content category
const getDefaultChecklist = (category: string): ChecklistItem[] => {
  const baseItems: ChecklistItem[] = [
    {
      id: '1',
      title: 'Content Planning',
      description: 'Research keywords and plan content structure',
      isCompleted: false,
    },
    {
      id: '2',
      title: 'Content Creation',
      description: 'Write and draft the content',
      isCompleted: false,
    },
    {
      id: '3',
      title: 'Review & Edit',
      description: 'Review content for accuracy and quality',
      isCompleted: false,
    },
    {
      id: '4',
      title: 'SEO Optimization',
      description: 'Optimize for search engines',
      isCompleted: false,
    },
    {
      id: '5',
      title: 'Final Approval',
      description: 'Get final approval from stakeholders',
      isCompleted: false,
    },
  ];

  // Add category-specific items
  if (category === 'Blog') {
    baseItems.splice(3, 0, {
      id: '3.5',
      title: 'Visual Content',
      description: 'Add images and visual elements',
      isCompleted: false,
    });
  } else if (category === 'LinkedIn Post') {
    baseItems.splice(2, 0, {
      id: '2.5',
      title: 'Hashtag Research',
      description: 'Research and add relevant hashtags',
      isCompleted: false,
    });
  }

  return baseItems;
};

const ChecklistDialog: React.FC<ChecklistDialogProps> = ({ open, onClose, task, onTaskUpdate }) => {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [seoImage, setSeoImage] = useState<File | null>(null);
  const [driveUrl, setDriveUrl] = useState('');
  const [postedUrl, setPostedUrl] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Edit mode state for Task Details
  const [isEditingTaskDetails, setIsEditingTaskDetails] = useState(false);
  const [editingSeoImage, setEditingSeoImage] = useState<File | null>(null);
  const [editingDriveUrl, setEditingDriveUrl] = useState('');

  const userRole = Cookies.get('role');
  const isSuperAdmin = userRole === 'superadmin';
  const isAdminOrSales = userRole === 'admin' || userRole === 'sales' || isSuperAdmin;
  const isSales = userRole === 'sales';

  // Debug logging
  console.log('ChecklistDialog - Task:', task);
  console.log('ChecklistDialog - User role:', userRole);
  console.log('ChecklistDialog - Is superadmin:', isSuperAdmin);
  console.log('ChecklistDialog - Task status:', task?.status);
  console.log('ChecklistDialog - Task seoScoreImage:', task?.seoScoreImage);
  console.log('ChecklistDialog - Task driveUrl:', task?.driveUrl);

  // Helper function to mark checklist items as completed based on task status
  const markChecklistAsCompleted = useCallback(
    (checklistItems: ChecklistItem[]): ChecklistItem[] => {
      if (task?.status === 'pending_approval' || task?.status === 'approved') {
        return checklistItems.map((item) => ({
          ...item,
          isCompleted: true,
          completedAt: item.completedAt || new Date().toISOString(),
        }));
      }
      return checklistItems;
    },
    [task?.status]
  );

  const fetchChecklistForTask = useCallback(async () => {
    if (!task) return;

    try {
      setLoading(true);
      // First try to get checklists from API for this category
      const response = await ContentTrackingService.getActiveChecklistsByCategory(task.category);

      if (response.status && response.data.length > 0) {
        // Convert API checklists to checklist items
        // Map template checklists and merge with task checklist to preserve completion status
        const apiChecklistItems: ChecklistItem[] = response.data.map((item) => {
          // Find matching item in task.checklist to preserve completion status
          const taskChecklistItem = task.checklist?.find(
            (tcItem) => tcItem.id === item._id || tcItem.title === item.title
          );

          return {
            id: item._id,
            title: item.title,
            description: item.description,
            // Preserve completion status from task.checklist if it exists
            isCompleted: taskChecklistItem?.isCompleted || false,
            completedAt: taskChecklistItem?.completedAt,
            completedBy: taskChecklistItem?.completedBy,
          };
        });

        // Use task checklist if it exists and has more items, otherwise use merged API checklist
        const taskChecklist =
          task.checklist &&
          task.checklist.length > 0 &&
          task.checklist.length >= apiChecklistItems.length
            ? task.checklist
            : apiChecklistItems;

        setChecklist(markChecklistAsCompleted(taskChecklist));
      } else {
        // Fallback to default checklist if no API checklists found
        const taskChecklist =
          task.checklist && task.checklist.length > 0
            ? task.checklist
            : getDefaultChecklist(task.category);

        setChecklist(markChecklistAsCompleted(taskChecklist));
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      // Fallback to default checklist on error
      // Preserve task checklist if it exists
      const taskChecklist =
        task.checklist && task.checklist.length > 0
          ? task.checklist
          : getDefaultChecklist(task.category);

      setChecklist(markChecklistAsCompleted(taskChecklist));
    } finally {
      setLoading(false);
    }
  }, [task, markChecklistAsCompleted]);

  useEffect(() => {
    if (task && open) {
      fetchChecklistForTask();

      // Reset form data
      setSeoImage(null);
      setDriveUrl('');
      setPostedUrl('');
      setIsEditingTaskDetails(false);
      setEditingSeoImage(null);
      setEditingDriveUrl(task?.driveUrl || '');
    }
  }, [task, open, fetchChecklistForTask]);

  const handleChecklistToggle = async (itemId: string) => {
    if (!task) return;

    // Find the item to toggle
    const itemToToggle = checklist.find((item) => item.id === itemId);
    if (!itemToToggle) return;

    const newCompletedStatus = !itemToToggle.isCompleted;
    const completedAt = newCompletedStatus ? new Date().toISOString() : undefined;

    // Optimistically update the UI
    const updatedChecklist = checklist.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          isCompleted: newCompletedStatus,
          completedAt: completedAt,
        };
      }
      return item;
    });

    setChecklist(updatedChecklist);

    // Call API to update the checklist item status
    try {
      // Check if task has checklist items initialized in the backend
      // If not, we need to initialize the checklist first using PUT (updateChecklist)
      // If yes, we can update individual item using PATCH (updateChecklistItemStatus)
      const taskHasChecklist = task.checklist && task.checklist.length > 0;

      if (!taskHasChecklist) {
        // Initialize checklist with all items first
        // This happens when checklist items come from templates but aren't saved to the task yet
        const checklistForInit = updatedChecklist.map((item) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          isCompleted: item.isCompleted,
          completedAt: item.completedAt,
          completedBy: item.completedBy,
        }));

        // Initialize the checklist on the task using PUT method
        await ContentTrackingService.updateChecklist(task._id, {
          checklist: checklistForInit,
        });
        showSnackbar(
          newCompletedStatus ? 'Checklist item marked as completed' : 'Checklist item unchecked',
          'success'
        );
      } else {
        // Task has checklist items initialized, update individual item using PATCH method
        await ContentTrackingService.updateChecklistItemStatus(
          task._id,
          itemId,
          newCompletedStatus,
          completedAt
        );
        showSnackbar(
          newCompletedStatus ? 'Checklist item marked as completed' : 'Checklist item unchecked',
          'success'
        );
      }

      // Refresh task data to ensure sync with backend
      // This will update task.checklist so subsequent toggles use the faster PATCH method
      onTaskUpdate();
    } catch (error) {
      console.error('Error updating checklist:', error);
      // Revert the change on error
      setChecklist(checklist);
      const errorMessage =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
        (error as { message?: string })?.message ||
        'Failed to update checklist item';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleRequestApproval = async () => {
    if (!task || !seoImage) {
      showSnackbar('Please provide an SEO image', 'error');
      return;
    }

    // Debug logging
    console.log('SEO Image:', seoImage);
    console.log('SEO Image type:', typeof seoImage);
    console.log('SEO Image instanceof File:', seoImage instanceof File);
    console.log('Drive URL:', driveUrl);

    try {
      setLoading(true);
      await ContentTrackingService.requestApproval(task._id, {
        seoImage,
        driveUrl: driveUrl.trim(),
      });
      showSnackbar('Approval requested successfully', 'success');
      onTaskUpdate();
    } catch (error) {
      console.error('Error requesting approval:', error);

      // Handle specific error messages from backend
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      if (axiosError.response?.data?.message) {
        showSnackbar(axiosError.response.data.message, 'error');
      } else if (axiosError.message) {
        showSnackbar(axiosError.message, 'error');
      } else {
        showSnackbar('Failed to request approval', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async () => {
    if (!task) return;

    try {
      setLoading(true);
      await ContentTrackingService.approveTask(task._id, true);
      showSnackbar('Task approved successfully', 'success');
      onTaskUpdate();
    } catch (error) {
      console.error('Error approving task:', error);
      showSnackbar('Failed to approve task', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async () => {
    if (!task || !postedUrl.trim()) {
      showSnackbar('Please provide the posted URL', 'error');
      return;
    }

    try {
      setLoading(true);
      await ContentTrackingService.updateTask(task._id, {
        status: 'completed',
        postedUrl: postedUrl.trim(),
      });
      showSnackbar('Task completed successfully', 'success');
      onTaskUpdate();
      onClose();
    } catch (error) {
      console.error('Error completing task:', error);
      showSnackbar('Failed to complete task', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('File selected:', file);
    console.log('File type:', file?.type);
    console.log('File size:', file?.size);

    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showSnackbar('Please select an image file', 'error');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('Image file size must be less than 5MB', 'error');
        return;
      }

      setSeoImage(file);
      console.log('SEO Image set successfully:', file.name);
    } else {
      console.log('No file selected');
    }
  };

  const handleEditImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showSnackbar('Please select an image file', 'error');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('Image file size must be less than 5MB', 'error');
        return;
      }

      setEditingSeoImage(file);
    }
  };

  const handleCompleteDirectTask = async () => {
    if (!task) return;

    try {
      setLoading(true);
      const response = await ContentTrackingService.updateTaskStatus(task._id, {
        status: 'completed',
      });

      if (response.status) {
        showSnackbar('Task completed successfully', 'success');
        onTaskUpdate();
        onClose();
      } else {
        showSnackbar(response.message || 'Failed to complete task', 'error');
      }
    } catch (error: any) {
      console.error('Error completing task:', error);
      showSnackbar(error.response?.data?.message || 'Failed to complete task', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTaskDetails = () => {
    setIsEditingTaskDetails(true);
    setEditingDriveUrl(task?.driveUrl || '');
    setEditingSeoImage(null);
  };

  const handleCancelEditTaskDetails = () => {
    setIsEditingTaskDetails(false);
    setEditingDriveUrl(task?.driveUrl || '');
    setEditingSeoImage(null);
  };

  const handleSaveTaskDetails = async () => {
    if (!task) return;

    // Check if at least one field has been changed
    const hasChanges = editingSeoImage || editingDriveUrl !== (task.driveUrl || '');

    if (!hasChanges) {
      showSnackbar('No changes to save', 'success');
      setIsEditingTaskDetails(false);
      return;
    }

    try {
      setLoading(true);
      await ContentTrackingService.updateTaskDetails(task._id, {
        seoImage: editingSeoImage || undefined,
        driveUrl: editingDriveUrl || undefined,
      });
      showSnackbar('Task details updated successfully', 'success');
      setIsEditingTaskDetails(false);
      onTaskUpdate();
    } catch (error) {
      console.error('Error updating task details:', error);
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      if (axiosError.response?.data?.message) {
        showSnackbar(axiosError.response.data.message, 'error');
      } else {
        showSnackbar('Failed to update task details', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const completedCount = checklist.filter((item) => item.isCompleted).length;
  const totalCount = checklist.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const canComplete = completedCount === totalCount && totalCount > 0;

  // Check if category is Email or Other - these don't use checklists
  const isDirectUploadCategory =
    task?.category === 'Email' || task?.category === 'Other' || task?.category === 'recurring_task';

  if (!task) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2b2c3e',
          color: '#fff',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          border: '1px solid rgba(104, 113, 209, 0.2)',
        },
      }}
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #23234a 0%, #1e1f2e 100%)',
          color: '#6871d1',
          fontWeight: 700,
          fontSize: '1.3rem',
          py: 2.5,
          px: 3,
          borderBottom: '2px solid rgba(104, 113, 209, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='h5' sx={{ fontWeight: 700, color: '#6871d1' }}>
            Task Checklist
          </Typography>
          <Chip
            label={`${completedCount}/${totalCount} completed`}
            color={canComplete ? 'success' : 'default'}
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              height: 32,
            }}
          />
        </Box>
        <Typography
          variant='subtitle1'
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.95rem',
            fontWeight: 500,
            mt: 0.5,
          }}
        >
          {task.title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ background: '#2b2c3e', p: 3 }}>
        {/* Only show checklist progress and items if not Email or Other category */}
        {!isDirectUploadCategory && (
          <>
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                backgroundColor: '#1e1e2f',
                borderRadius: 3,
                mt: 2,
                border: '1px solid rgba(104, 113, 209, 0.2)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                }}
              >
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem' }}
                >
                  Progress
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 700, color: '#6871d1', fontSize: '1rem' }}
                >
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={progress}
                color={canComplete ? 'success' : 'primary'}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                  },
                }}
              />
            </Box>

            {!canComplete && completedCount > 0 && (
              <Alert
                severity='info'
                sx={{
                  mb: 3,
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  border: '1px solid rgba(33, 150, 243, 0.3)',
                  color: '#90caf9',
                  borderRadius: 2,
                }}
              >
                Complete all checklist items to mark this task as finished.
              </Alert>
            )}

            <Box
              sx={{
                backgroundColor: '#1e1e2f',
                borderRadius: 3,
                border: '1px solid rgba(104, 113, 209, 0.2)',
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <List sx={{ p: 0 }}>
                {checklist.map((item, index) => (
                  <ListItem
                    key={item.id}
                    button
                    onClick={() => handleChecklistToggle(item.id)}
                    disabled={
                      loading || task?.status === 'pending_approval' || task?.status === 'approved'
                    }
                    sx={{
                      py: 1.5,
                      px: 2.5,
                      borderBottom:
                        index < checklist.length - 1
                          ? '1px solid rgba(104, 113, 209, 0.1)'
                          : 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(104, 113, 209, 0.1)',
                      },
                      '&:disabled': {
                        opacity: 0.7,
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <Checkbox
                        checked={item.isCompleted}
                        icon={
                          <UncheckedIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 28 }} />
                        }
                        checkedIcon={<CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />}
                        disabled={
                          loading ||
                          task?.status === 'pending_approval' ||
                          task?.status === 'approved'
                        }
                        sx={{
                          '&.Mui-checked': {
                            color: '#4caf50',
                          },
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant='body1'
                          sx={{
                            textDecoration: item.isCompleted ? 'line-through' : 'none',
                            color: item.isCompleted ? 'rgba(255, 255, 255, 0.5)' : '#fff',
                            fontWeight: item.isCompleted ? 400 : 500,
                            fontSize: '0.95rem',
                            mb: 0.5,
                          }}
                        >
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant='body2'
                            sx={{
                              textDecoration: item.isCompleted ? 'line-through' : 'none',
                              color: item.isCompleted
                                ? 'rgba(255, 255, 255, 0.3)'
                                : 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                            }}
                          >
                            {item.description}
                          </Typography>
                          {item.isCompleted && item.completedAt && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: '#4caf50',
                                fontSize: '0.75rem',
                                display: 'block',
                                mt: 0.5,
                                fontWeight: 500,
                              }}
                            >
                              ✓ Completed on {dayjs(item.completedAt).format('MMM DD, YYYY HH:mm')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        {/* Unified Approval Section - Show for all pending tasks */}
        {task?.status === 'pending' && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              backgroundColor: '#1e1e2f',
              borderRadius: 3,
              border: '1px solid rgba(104, 113, 209, 0.3)',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.1)',
            }}
          >
            <Typography
              variant='h6'
              sx={{
                mb: 2.5,
                color: '#6871d1',
                fontWeight: 700,
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {isDirectUploadCategory ? (
                <UploadIcon sx={{ fontSize: 20 }} />
              ) : (
                <SendIcon sx={{ fontSize: 20 }} />
              )}
              {isDirectUploadCategory ? 'Upload Content' : 'Request Approval'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {isDirectUploadCategory && task?.bypassUpload ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    This task can be completed directly without proof upload.
                  </Typography>
                  <Button
                    variant='contained'
                    onClick={handleCompleteDirectTask}
                    disabled={loading}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                      backgroundColor: '#4caf50',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      '&:hover': {
                        backgroundColor: '#45a049',
                        boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                      },
                    }}
                  >
                    Complete Task
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {isDirectUploadCategory
                      ? 'Upload image and provide drive URL to request approval.'
                      : canComplete
                        ? 'All checklist items completed. Upload image and provide drive URL to request approval.'
                        : 'Upload seo image and provide drive URL to request approval.'}
                  </Typography>

                  <Box>
                    <input
                      accept='image/*'
                      style={{ display: 'none' }}
                      id='seo-image-upload'
                      type='file'
                      onChange={handleImageUpload}
                    />
                    <label htmlFor='seo-image-upload'>
                      <Button
                        variant='outlined'
                        component='span'
                        startIcon={<UploadIcon />}
                        disabled={loading}
                        sx={{
                          borderColor: 'rgba(104, 113, 209, 0.5)',
                          color: '#6871d1',
                          textTransform: 'none',
                          fontWeight: 500,
                          px: 3,
                          py: 1,
                          '&:hover': {
                            borderColor: '#6871d1',
                            backgroundColor: 'rgba(104, 113, 209, 0.1)',
                          },
                        }}
                      >
                        {seoImage ? seoImage.name : 'Upload Image'}
                      </Button>
                    </label>
                    {seoImage && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: '#4caf50',
                          ml: 2,
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        ✓ {seoImage.name}
                      </Typography>
                    )}
                  </Box>

                  {task?.category !== 'Email' && (
                    <TextField
                      label='Drive URL'
                      value={driveUrl}
                      onChange={(e) => setDriveUrl(e.target.value)}
                      fullWidth
                      placeholder='https://drive.google.com/...'
                      disabled={loading}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': {
                            borderColor: 'rgba(104, 113, 209, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: '#6871d1',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6871d1',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  )}

                  <Button
                    variant='contained'
                    onClick={handleRequestApproval}
                    disabled={
                      !seoImage || loading || (task?.category !== 'Email' && !driveUrl.trim())
                    }
                    startIcon={<SendIcon />}
                    sx={{
                      backgroundColor: '#6871d1',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 12px rgba(104, 113, 209, 0.3)',
                      '&:hover': {
                        backgroundColor: '#5a63bb',
                        boxShadow: '0 6px 16px rgba(104, 113, 209, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(104, 113, 209, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    Request Approval
                  </Button>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* View Section - Show task details for all users */}
        {(() => {
          const shouldShowView = task && (task?.seoScoreImage || task?.driveUrl || task?.postedUrl);
          console.log('ChecklistDialog - Should show view section:', shouldShowView);
          return shouldShowView;
        })() && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              backgroundColor: '#1e1e2f',
              borderRadius: 3,
              border: '1px solid rgba(104, 113, 209, 0.3)',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.1)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2.5,
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  color: '#6871d1',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                }}
              >
                Task Details
              </Typography>
              {!isEditingTaskDetails &&
                task?.status !== 'approved' &&
                task?.status !== 'completed' &&
                (task?.seoScoreImage || task?.driveUrl) && (
                  <Tooltip title='Edit Task Details'>
                    <IconButton
                      onClick={handleEditTaskDetails}
                      disabled={loading}
                      sx={{
                        backgroundColor: '#6871d1',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#5a63bb',
                        },
                        '&:disabled': {
                          backgroundColor: '#aeb3e9',
                          color: '#fff',
                          opacity: 0.7,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>

            {!isEditingTaskDetails ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {task?.seoScoreImage && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#23234a',
                      borderRadius: 2,
                      border: '1px solid rgba(104, 113, 209, 0.2)',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                      }}
                    >
                      Image:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: '#2b2c3e',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <img
                        src={task.seoScoreImage}
                        alt='SEO Score'
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {task?.driveUrl && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#23234a',
                      borderRadius: 2,
                      border: '1px solid rgba(104, 113, 209, 0.2)',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                      }}
                    >
                      Drive URL:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#6871d1',
                        fontSize: '0.9rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      <a
                        href={task.driveUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: '#6871d1', textDecoration: 'none' }}
                      >
                        {task.driveUrl}
                      </a>
                    </Typography>
                  </Box>
                )}

                {task?.postedUrl && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#23234a',
                      borderRadius: 2,
                      border: '1px solid rgba(104, 113, 209, 0.2)',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                      }}
                    >
                      Posted URL:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#6871d1',
                        fontSize: '0.9rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      <a
                        href={task.postedUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: '#6871d1', textDecoration: 'none' }}
                      >
                        {task.postedUrl}
                      </a>
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* SEO Image Edit */}
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: '#23234a',
                    borderRadius: 2,
                    border: '1px solid rgba(104, 113, 209, 0.2)',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: '#fff',
                      fontSize: '0.9rem',
                    }}
                  >
                    Image:
                  </Typography>
                  {task?.seoScoreImage && !editingSeoImage && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2,
                          backgroundColor: '#2b2c3e',
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          mb: 1.5,
                        }}
                      >
                        <img
                          src={task.seoScoreImage}
                          alt='Current SEO Score'
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.8rem',
                        }}
                      >
                        Current image (upload new to replace)
                      </Typography>
                    </Box>
                  )}
                  {editingSeoImage && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 2,
                          backgroundColor: '#2b2c3e',
                          borderRadius: 2,
                          border: '2px solid #4caf50',
                          mb: 1.5,
                        }}
                      >
                        <img
                          src={URL.createObjectURL(editingSeoImage)}
                          alt='New SEO Score Preview'
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Typography
                        variant='caption'
                        sx={{
                          color: '#4caf50',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                        }}
                      >
                        New image: {editingSeoImage.name}
                      </Typography>
                    </Box>
                  )}
                  <input
                    accept='image/*'
                    style={{ display: 'none' }}
                    id='edit-seo-image-upload'
                    type='file'
                    onChange={handleEditImageUpload}
                    disabled={task?.status === 'approved' || task?.status === 'completed'}
                  />
                  <label htmlFor='edit-seo-image-upload'>
                    <Button
                      variant='outlined'
                      component='span'
                      size='small'
                      startIcon={<UploadIcon />}
                      disabled={
                        loading || task?.status === 'approved' || task?.status === 'completed'
                      }
                      sx={{
                        borderColor: 'rgba(104, 113, 209, 0.5)',
                        color: '#6871d1',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#6871d1',
                          backgroundColor: 'rgba(104, 113, 209, 0.1)',
                        },
                      }}
                    >
                      {editingSeoImage ? 'Change Image' : 'Upload New Image'}
                    </Button>
                  </label>
                </Box>

                {/* Drive URL Edit */}
                <TextField
                  label='Drive URL'
                  value={editingDriveUrl}
                  onChange={(e) => setEditingDriveUrl(e.target.value)}
                  fullWidth
                  placeholder='https://drive.google.com/...'
                  disabled={loading || task?.status === 'approved' || task?.status === 'completed'}
                  helperText='Enter the Google Drive URL for this task'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(104, 113, 209, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#6871d1',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#6871d1',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant='outlined'
                    onClick={handleCancelEditTaskDetails}
                    disabled={loading}
                    startIcon={<CancelIcon />}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'rgba(255, 255, 255, 0.8)',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 3,
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    onClick={handleSaveTaskDetails}
                    disabled={
                      loading || task?.status === 'approved' || task?.status === 'completed'
                    }
                    startIcon={<SaveIcon />}
                    sx={{
                      backgroundColor: '#6871d1',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      boxShadow: '0 4px 12px rgba(104, 113, 209, 0.3)',
                      '&:hover': {
                        backgroundColor: '#5a63bb',
                        boxShadow: '0 6px 16px rgba(104, 113, 209, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(104, 113, 209, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Approval Section - Show when task is pending_approval and user is superadmin */}
        {(() => {
          const shouldShowApproval = task?.status === 'pending_approval' && isAdminOrSales;
          console.log('ChecklistDialog - Should show approval section:', shouldShowApproval);
          return shouldShowApproval;
        })() && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              backgroundColor: '#1e1e2f',
              borderRadius: 3,
              border: '1px solid rgba(76, 175, 80, 0.3)',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
            }}
          >
            <Typography
              variant='h6'
              sx={{
                mb: 2.5,
                color: '#4caf50',
                fontWeight: 700,
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <ThumbUpIcon sx={{ fontSize: 20 }} />
              Approve Task
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}
              >
                Review the submitted content and approve the task
              </Typography>

              <Button
                variant='contained'
                onClick={handleApproveTask}
                disabled={loading || isSales}
                startIcon={<ThumbUpIcon />}
                sx={{
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    backgroundColor: '#45a049',
                    boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(76, 175, 80, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Approve Task
              </Button>
            </Box>
          </Box>
        )}

        {/* Complete Section - Show when task is approved */}
        {task && task?.status === 'approved' && (
          <Box
            sx={{
              mt: 2,
              p: 3,
              backgroundColor: '#1e1e2f',
              borderRadius: 3,
              border: '1px solid rgba(76, 175, 80, 0.3)',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
            }}
          >
            <Typography
              variant='h6'
              sx={{
                mb: 2.5,
                color: '#4caf50',
                fontWeight: 700,
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20 }} />
              Complete Task
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}
              >
                Task has been approved. Provide the final posted URL to complete.
              </Typography>

              <TextField
                label='Posted URL'
                value={postedUrl}
                onChange={(e) => setPostedUrl(e.target.value)}
                fullWidth
                placeholder='https://linkedin.com/posts/...'
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(104, 113, 209, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#6871d1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6871d1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />

              <Button
                variant='contained'
                onClick={handleCompleteTask}
                disabled={!postedUrl.trim() || loading || !task?.isApprove}
                startIcon={<CheckCircleIcon />}
                sx={{
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    backgroundColor: '#45a049',
                    boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(76, 175, 80, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Complete Task
              </Button>

              {!task?.isApprove && (
                <Alert
                  severity='warning'
                  sx={{
                    mt: 1,
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    color: '#ffb74d',
                    borderRadius: 2,
                  }}
                >
                  Task must be approved before it can be completed.
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          background: '#23234a',
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: '1px solid rgba(104, 113, 209, 0.2)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            backgroundColor: 'transparent',
            color: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            py: 1.25,
            borderRadius: '8px',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            px: 3,
            fontSize: '0.95rem',
            textTransform: 'none',
            minWidth: 100,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: 'rgba(255, 255, 255, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              opacity: 0.6,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ChecklistDialog;
